// src/oura/components/NotificationBell.tsx
//
// The notification centre, one component for both audiences. The merchant's
// copy sits in the merchant shell (the sidebar foot, or the phone's bottom
// bar); the staff copy sits on the Client screen. Same list, same read state,
// different audience.

import { useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { BellIcon } from "lucide-react";
import { listNotifications, markNotificationRead, type Audience, type Notification } from "@service/aeo/clientReview";
import { FOCUS_RING, TYPE, useEscapeLayer } from "@oura/ui";
import { cn } from "../lib/cn";
import { useVisiblePoll } from "../lib/useVisiblePoll";

function ago(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.round(ms / 60000);
    if (m < 1) return "now";
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

const GAP = 8;
/**
 * The row's focus outline, drawn inside the row: the list scrolls and the menu
 * clips, so an outward ring (FOCUS_RING) is cut off. One complete string, never
 * FOCUS_RING plus an override, since cn does not merge conflicting classes.
 */
const ROW_FOCUS = "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600";
/** The menu's width on a desktop. */
const MENU_W = 400;
/** The side gutter the menu keeps from the viewport's edges: on a phone it runs full width inside it. */
const EDGE = 16;

/**
 * Where the menu goes, from the bell's own rectangle, clamped to the viewport.
 *
 * `side`: to the right of the sidebar, its foot level with the bell's, so a
 * short list sits beside the bell rather than high above it. `sideTop`: the
 * same, for a bell at the top of the rail: its top level with the bell's, and
 * it grows down. `up`: above the
 * phone's bottom bar. No rail: under the bell, lined up with its `align` edge.
 * The height is capped to the room there is, and the list scrolls inside.
 */
type BellRail = "side" | "sideTop" | "up";

function placeMenu(b: DOMRect, rail: BellRail | undefined, align: "left" | "right"): CSSProperties {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(MENU_W, vw - 2 * EDGE);
    const left = (x: number) => Math.max(EDGE, Math.min(x, vw - width - EDGE));
    if (rail === "side") {
        const bottom = Math.max(GAP, vh - b.bottom);
        // Capped to the room above the foot, so a tall list in a short window keeps a gap at the top.
        return { width, left: left(b.right + GAP), bottom, maxHeight: vh - bottom - GAP };
    }
    if (rail === "sideTop") {
        const top = Math.max(GAP, b.top);
        return { width, left: left(b.right + GAP), top, maxHeight: vh - top - GAP };
    }
    if (rail === "up") return { width, left: left(b.right - width), bottom: vh - b.top + GAP, maxHeight: b.top - 2 * GAP };
    const top = b.bottom + 4;
    return { width, left: left(align === "right" ? b.right - width : b.left), top, maxHeight: vh - top - GAP };
}

export function NotificationBell({
    audience,
    businessId,
    onOpen,
    align = "right",
    rail,
}: {
    audience: Audience;
    businessId: string;
    /**
     * A notice was chosen, after it is marked read. The merchant shell sends a
     * notice about one ask to that ask on Decisions and any other to Home.
     */
    onOpen?: (n: Notification) => void;
    /** Under the bell (no `rail`): which of the bell's edges the menu lines up with. */
    align?: "left" | "right";
    /**
     * In the merchant shell: a 44px icon target, and the menu opens to the
     * right of the sidebar (`side`) or above the phone's bottom bar (`up`).
     */
    rail?: BellRail;
}) {
    const [items, setItems] = useState<Notification[]>([]);
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuId = useId();
    const [pos, setPos] = useState<CSSProperties | null>(null);

    // Portalled to the body and fixed from the bell's rectangle. The sidebar
    // is sticky, and a sticky box is its own stacking context: a menu inside
    // it, whatever its z-index, painted under the page's cards. The sidebar
    // also scrolls, which clips anything hung off it absolutely.
    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        const measure = () => {
            const b = btnRef.current?.getBoundingClientRect();
            if (b) setPos(placeMenu(b, rail, align));
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [open, rail, align]);

    /** Close, and put focus back on the bell that opened the menu. */
    const close = () => {
        setOpen(false);
        btnRef.current?.focus();
    };
    useEscapeLayer(close, open);

    // The bell is decoration when it fails: an unreachable notifications endpoint
    // must not take the header down with an unhandled rejection.
    const refresh = () => { void listNotifications(audience, businessId).then(setItems).catch(() => undefined); };
    // Was a 4-second interval, which the in-process mock made free and the real
    // API does not (#3676). Fetching when the tab comes back to the front is
    // what a reader actually wants; the minute tick is only so a tab left open
    // on a second screen still moves.
    useVisiblePoll(refresh, 60_000, [audience, businessId]);

    const unread = items.filter((n) => !n.read).length;
    const click = async (n: Notification) => {
        if (!n.read) { await markNotificationRead(n.id); refresh(); }
        setOpen(false);
        onOpen?.(n);
    };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                className={cn(
                    "relative grid place-items-center transition-colors",
                    rail
                        ? cn(rail !== "up" ? "h-9 w-9" : "h-11 w-11", "rounded-lg", open ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-blue-50 hover:text-blue-600")
                        : "h-9 w-9 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                    FOCUS_RING,
                )}
            >
                <span className="relative">
                    <BellIcon aria-hidden className={rail === "up" ? "h-6 w-6" : "h-5 w-5"} />
                    {unread > 0 && (
                        <span aria-hidden className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold leading-none text-white tabular-nums">{unread}</span>
                    )}
                </span>
            </button>

            {open && pos && createPortal(
                <>
                    <div aria-hidden className="fixed inset-0 z-30" onClick={close} />
                    <div
                        id={menuId}
                        role="region"
                        aria-label="Updates"
                        style={pos}
                        className="fixed z-40 flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_2px_6px_rgba(0,0,0,0.06),0_16px_40px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]"
                    >
                        <div className="flex flex-shrink-0 items-baseline justify-between gap-3 border-b border-gray-100 px-5 pb-3 pt-4">
                            <span className={cn(TYPE.tt, "text-gray-900")}>Updates</span>
                            <span className={cn(TYPE.tc, "tabular-nums", unread ? "font-medium text-blue-600" : "text-gray-400")}>{unread ? `${unread} unread` : "All read"}</span>
                        </div>
                        {/* Its own scroll: about six notices show, the rest scroll inside the menu. */}
                        <div className="max-h-[480px] min-h-0 flex-1 overflow-y-auto overscroll-contain">
                            {items.length === 0 ? (
                                <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
                                    <BellIcon aria-hidden className="h-6 w-6 text-gray-300" />
                                    <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No updates yet.</p>
                                </div>
                            ) : items.map((n) => (
                                <button
                                    type="button"
                                    key={n.id}
                                    onClick={() => void click(n)}
                                    className={cn(
                                        "flex w-full items-start gap-3 border-t border-gray-100 px-5 py-3.5 text-left transition-colors first:border-t-0",
                                        n.read ? "hover:bg-gray-50" : "bg-blue-50/50 hover:bg-blue-50",
                                        ROW_FOCUS,
                                    )}
                                >
                                    {/* The unread dot, level with the title's first line; a read notice keeps the space so every title starts in one column. */}
                                    <span aria-hidden className={cn("mt-1.5 h-2 w-2 flex-shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-blue-600")} />
                                    <span className="min-w-0 flex-1">
                                        <span className={cn(TYPE.tb, "block break-words font-medium leading-5 text-gray-900")}>
                                            {!n.read && <span className="sr-only">Unread: </span>}
                                            {n.title}
                                        </span>
                                        {n.body && <span className={cn(TYPE.tc, "mt-0.5 line-clamp-2 break-words text-gray-600")}>{n.body}</span>}
                                    </span>
                                    <span className={cn(TYPE.tm, "flex-shrink-0 leading-5 text-gray-400 tabular-nums")}>{ago(n.at)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </>,
                document.body,
            )}
        </>
    );
}
